import { toast } from '@qodo/design-system';
import { useMutation } from '@tanstack/react-query';
import { transformKeysToCamelCase } from '../../utils/caseTransformers';
import { spawn } from '../../utils/translations';
import { AzureDevOpsClient } from '../httpClients/AzureDevOpsClient';
import { SyncRepositoriesResponse } from '../httpClients/AzureDevOpsClient/api.schema';
import { QUERY_KEYS } from '../queries/consts';
import { queryClient } from '../queryClient';
import { MUTATION_KEYS } from './consts';
import { getErrorToastContent } from './utils';

type SyncRepositoriesResponseRaw = SyncRepositoriesResponse & {
  synced_count?: number;
  removed_count?: number;
  total_selected?: number;
};

export const useSyncAzureRepositoriesMutation = () => {
  const spawnToasts = spawn('toasts.azure.sync-repositories');

  return useMutation({
    mutationKey: [MUTATION_KEYS.SYNC_AZURE_REPOSITORIES],
    mutationFn: async ({
      repositoryIds,
      azureTenantId,
    }: {
      repositoryIds: string[];
      azureTenantId?: string;
    }): Promise<SyncRepositoriesResponse> => {
      const response = await AzureDevOpsClient.syncRepositories({
        repositoryIds,
        azureTenantId,
      });

      return transformKeysToCamelCase<
        SyncRepositoriesResponse,
        SyncRepositoriesResponseRaw
      >(response as SyncRepositoriesResponseRaw);
    },
    onSuccess: (data) => {
      toast.success(data.message || spawnToasts('success'));
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.AZURE_AVAILABLE_REPOSITORIES],
      });
    },
    onError: (err: any) => {
      const errorCode = err?.response?.data?.code;
      const errorMessage = err?.response?.data?.detail || err?.message || '';

      const isTokenExpired =
        errorCode === 'REFRESH_TOKEN_EXPIRED' ||
        errorMessage.includes('reconnect your Azure DevOps account');

      if (isTokenExpired) {
        toast.error(spawnToasts('session-expired'), { duration: 7000 });
      } else {
        toast.error(getErrorToastContent(err, spawnToasts('error')));
      }
    },
  });
};
